import { IUsersWorkplace } from 'core/interfaces/userInterface';
import { apiService } from 'core/services/apiService';
import { User, UserMe } from 'types';
import { Participant } from './broadcast';

/**
 * Fetches the list of users belonging to a specific workplace.
 * @param workplaceId - The numeric ID of the workplace.
 * @returns An array of {@link User} objects.
 */
const GetUsersList = async (workplaceId: number): Promise<User[]> => {
	const { ...data } = apiService.responseHandler(
		await apiService.get(`users/${workplaceId}`)
	);
	return [data];
};

/**
 * Fetches the list of workplaces associated with the current user.
 * @returns An array of {@link IUsersWorkplace} objects.
 */
const GetUsersWorkplacesList = async (): Promise<IUsersWorkplace[]> => {
	const { ...data } = apiService.responseHandler(
		await apiService.get(`users/workplaces`)
	);
	return [data];
};

/**
 * Fetches users assigned to a workplace and maps them to the {@link Participant} shape
 * used by the broadcast and messaging features.
 * @param workplaceId - The numeric ID of the workplace.
 * @returns An array of {@link Participant} objects with trimmed name fields.
 */
const GetParticipantsList = async (workplaceId: number): Promise<Participant[]> => {
	const { ...data } = apiService.responseHandler(
		await apiService.get(`users-workplaces/${workplaceId}`)
	);

	const participants: Array<Participant> = Object.values(data as Array<IUsersWorkplace>).map((user) => ({
		id: user.user.id,
		first_name: user.user.first_name.trim(),
		last_name: user.user.last_name.trim(),
		email: user.user.email || '',
	}));

	return participants;
};

/**
 * Fetches a single user's profile by ID.
 * @param userId - The numeric ID of the user.
 * @returns The {@link UserMe} profile object.
 */
const getUser = async (userId: number): Promise<UserMe> => {
	const { data } = apiService.responseHandler(await apiService.get(`users/${userId}`));
	return data;
};

export { GetParticipantsList, getUser, GetUsersList, GetUsersWorkplacesList };
